import {
  DefaultButton,
  PrimaryButton,
  Modal,
  Stack,
  getTheme
} from "@fluentui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import * as Yup from "yup";

import { ContractsService } from "@/core/libs/api";
import { FetchResponse } from "@/core/libs/api/utils/fetchData";
import FormBuilder from "@/core/libs/form-builder";
import { IMessageBar, messageBarTypes } from "@/core/libs/message-bar";
import { AttachmentTypes, fields } from "@/modules/Contracts/fields/fields";

interface IFileWithAttachment extends File {
  attachmentType?: AttachmentTypes;
}

interface IFormValues {
  files: IFileWithAttachment[];
}

interface DialogProps {
  requestContract: () => Promise<FetchResponse>;
  closeDocumentModal: () => void;
  isVisibleDocumentModal: boolean;
  contractId: string;
}

const schema = Yup.object({
  files: Yup.array()
    .of(Yup.mixed<IFileWithAttachment>().required())
    .min(1, "Selecione pelo menos um arquivo")
    .required("Campo obrigatório.")
});

export function UploadDocumentModal({
  closeDocumentModal,
  requestContract,
  isVisibleDocumentModal,
  contractId
}: DialogProps): JSX.Element {
  const uploadDocumentFormId: string = "upload-document-form";
  const [documentsMessage, setDocumentsMessage] =
    useState<IMessageBar>(undefined);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const accountsQueryClient = useQueryClient();

  const theme = getTheme();

  function clearMessage() {
    setDocumentsMessage(null);
  }

  function closeModal() {
    clearMessage();
    closeDocumentModal();
  }

  async function requestAndSetUploads() {
    try {
      await requestContract();

      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch {
      setDocumentsMessage({
        message:
          "Não foi possível localizar o contrato, por favor atualize a página e tente novamente",
        type: messageBarTypes.ERROR
      });
    }
  }

  async function uploadDocument({
    contractId,
    data
  }: {
    contractId: number;
    data: FormData;
  }) {
    const abortController = new AbortController();
    const { signal } = abortController;
    const service = ContractsService();

    const params = {
      id: contractId,
      body: data
    };

    const response = await service.upload({ params, signal });

    if (response.error) {
      setDocumentsMessage({
        message:
          response.error.message ?? "Não foi possível anexar, tente novamente",
        type: messageBarTypes.ERROR
      });

      throw new Error("Não foi possível anexar os documentos");
    } else {
      setDocumentsMessage({
        message: "Documento(s) anexados(s) com sucesso.",
        type: messageBarTypes.SUCCESS
      });
    }
  }

  async function handleSubmitForm(
    values: IFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) {
    if (values.files?.length > 0) {
      clearMessage();
      setIsUploading(true);

      for (const file of values.files) {
        if (
          !file?.attachmentType &&
          !fields.uploadDocument.find(f => !f.attachmentTypeConfig)
        ) {
          setDocumentsMessage({
            message: "Selecione o tipo do anexo e tente novamente.",
            type: messageBarTypes.ERROR
          });
          setIsUploading(false);
          setSubmitting(false);

          return;
        }
      }
      const dataToSubmit = new FormData();

      values.files.forEach((file, index) => {
        dataToSubmit.append("files[]", file);
        dataToSubmit.append(`attachmentType[${index}]`, file.attachmentType);
      });

      try {
        await uploadDocument({
          data: dataToSubmit,
          contractId: parseInt(contractId, 10)
        });

        await requestAndSetUploads();
      } finally {
        setIsUploading(false);
        setSubmitting(false);

        await accountsQueryClient.invalidateQueries({
          queryKey: ["contract-uploads"]
        });
      }
    }
  }

  return (
    <Modal
      isOpen={isVisibleDocumentModal}
      onDismiss={closeModal}
      styles={{ main: { minWidth: "550px", padding: "32px" } }}
      titleAriaId="upload-document-modal-title"
    >
      <h4
        id="upload-document-modal-title"
        style={{
          fontSize: theme.fonts.xLarge.fontSize,
          fontWeight: theme.fonts.xLarge.fontWeight
        }}
      >
        Anexar documento(s) ao contrato
      </h4>

      <FormBuilder
        dismissMessage={clearMessage}
        onSubmit={handleSubmitForm}
        dismissPanel={closeModal}
        formId={uploadDocumentFormId}
        fields={fields.uploadDocument}
        message={documentsMessage}
        validationSchema={schema}
      >
        <Stack
          as="footer"
          horizontal
          horizontalAlign="end"
          tokens={{ childrenGap: 8 }}
        >
          <DefaultButton
            onClick={closeModal}
            text="Cancelar"
            id="button-cancel-upload-documents"
          />
          <PrimaryButton
            form={uploadDocumentFormId}
            text="Anexar"
            id="button-submit-documents"
            disabled={isUploading}
            type="submit"
          />
        </Stack>
      </FormBuilder>
    </Modal>
  );
}
